import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import HomeButton from '../components/HomeButton';
import { ArrowLeft, MapPin, Plus, Trash2, Building2, Edit, Save, X, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GestionarLugares = () => {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const [cliente, setCliente] = useState(null);
  const [lugares, setLugares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });

  useEffect(() => {
    if (clienteId) {
      fetchCliente();
      fetchLugares();
    }
  }, [clienteId]);

  const fetchCliente = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/clientes-empresa/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCliente(response.data);
    } catch (err) {
      setError('Error al cargar cliente');
      console.error(err);
    }
  };

  const fetchLugares = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/lugares?cliente_id=${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLugares(response.data);
    } catch (err) {
      setError('Error al cargar lugares');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/lugares`, {
        ...formData,
        cliente_id: clienteId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({ nombre: '', descripcion: '' });
      fetchLugares();
    } catch (err) {
      setError('Error al crear lugar');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (lugar) => {
    setEditingId(lugar.id);
    setEditData({ 
      nombre: lugar.nombre, 
      descripcion: lugar.descripcion || '',
      cliente_id: lugar.cliente_id
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSaveEdit = async (lugarId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/lugares/${lugarId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingId(null);
      setEditData({});
      fetchLugares();
    } catch (err) {
      setError('Error al actualizar lugar');
      console.error(err);
    }
  };

  const handleDelete = async (lugarId) => {
    if (!window.confirm('¿Estás seguro de eliminar este lugar?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/lugares/${lugarId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLugares();
    } catch (err) {
      setError('Error al eliminar lugar');
      console.error(err);
    }
  };

  const filteredLugares = lugares.filter(lugar =>
    lugar.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lugar.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <HomeButton />
        </div>

        {cliente && (
          <div className="mb-4 p-4 bg-white rounded-lg border flex items-center gap-3">
            <Building2 className="h-6 w-6 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Gestionando lugares de:</p>
              <p className="font-bold text-lg">{cliente.nombre}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulario */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Crear Nuevo Lugar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre del Lugar *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Sucursal Centro"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Input
                    id="descripcion"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    placeholder="Descripción opcional"
                  />
                </div>

                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? 'Guardando...' : 'Crear Lugar'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Lista de Lugares */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Lugares ({filteredLugares.length})
              </CardTitle>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto"></div>
                </div>
              ) : filteredLugares.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {searchTerm ? 'No se encontraron lugares' : 'No hay lugares registrados para este cliente'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredLugares.map((lugar) => (
                    <div key={lugar.id} className="p-3 bg-gray-50 rounded-lg border">
                      {editingId === lugar.id ? (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input
                              value={editData.nombre}
                              onChange={(e) => setEditData({ ...editData, nombre: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Descripción</Label>
                            <Input
                              value={editData.descripcion}
                              onChange={(e) => setEditData({ ...editData, descripcion: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveEdit(lugar.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-4 w-4 mr-1" />
                              Guardar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium">{lugar.nombre}</p>
                            {lugar.descripcion && (
                              <p className="text-sm text-gray-600 mt-1">{lugar.descripcion}</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(lugar)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(lugar.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GestionarLugares;
